import { useAppContext } from '@/context';
import { postJson } from '@/utils/fetchOptions';
import { synchronizeLanguage, hasUrlLanguagePrefix } from '@/utils/languages';
import { updateAuthInStorage } from '@/utils/auth';
import { setBugsnagUser } from '@/utils/bugsnag';

import { useMutation } from './useMutation';

interface UseLoginJwtProps {
  token: string;
  onSuccess?: TypeAny;
}

export function useLoginJwt({ token, onSuccess }: UseLoginJwtProps) {
  const { setAlert } = useAppContext();

  const {
    isLoading,
    data = {},
    mutate,
    error
  } = useMutation({
    mutationKey: `authenticate-with-token-${token}`,
    mutationFn: async () =>
      postJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/authenticate/with-token`,
        body: { token }
      }).then(
        (
          response:
            | { token: string; user: Api.User; code: 'ACCOUNT_REGISTERED' | 'ACCOUNT_LOGIN' }
            | ResponseObject
        ) => {
          if ('code' in response && !('token' in response && 'user' in response)) {
            setAlert(response.code);
          }

          if ('token' in response && 'user' in response) {
            const { token: jwtToken, user, code } = response;

            updateAuthInStorage({
              token: jwtToken,
              user: user.user,
              code
            });

            // Update Bugsnag user identification
            setBugsnagUser(user.user);

            // Update language if user has a language preference and no URL language is specified
            if (!hasUrlLanguagePrefix() && user.user?.language) {
              synchronizeLanguage(user.user.language, 'login-jwt');
            }
          }

          return response;
        }
      ),
    onSuccess
  });

  return {
    isLoading,
    mutate,
    error,
    data
  };
}
